import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, DataTable, Drawer, ProgressBar, Stack, t, Text, Timeline } from '@kanbios/ui';
import { FULL_HEIGHT } from '../../lib/tables';
import type { provisioning } from '@kanbios/api-client';
import { api, unwrap } from '../../lib/api';

type Job = provisioning.components['schemas']['EnrolmentJob'];
type Progress = provisioning.components['schemas']['ServerProgress'];
const RESULT_TONES = { queued: 'neutral', running: 'info', succeeded: 'pos', failed: 'neg' } as const;

/** An enrolment job, polled every 3 s while it waits or runs (RG-007-5). */
export function useEnrolmentJob(jobId: string | null) {
  return useQuery({
    queryKey: ['enrolment-job', jobId],
    enabled: Boolean(jobId),
    queryFn: async () => unwrap(await api.provisioning.GET('/jobs/{job}', { params: { path: { job: jobId! } } })),
    refetchInterval: query => (query.state.data?.status === 'running' || query.state.data?.status === 'queued' ? 3_000 : false),
  });
}

export const failedServers = (job: Job | undefined) => (job?.servers ?? []).filter(s => s.status !== 'succeeded').map(s => s.server);

/**
 * Follow-up of an enrolment (SPEC-007 « Enrôlement »): servers with their
 * progress and result; a server opens its tasks in a Timeline (Drawer md).
 */
export function EnrolmentProgress({ job, loading, height = FULL_HEIGHT, results = true }: {
  job: Job | undefined;
  loading: boolean;
  height?: number;
  /** false: the page says the outcome itself (the wizard's « et maintenant ? » heading). */
  results?: boolean;
}) {
  const [server, setServer] = useState<string | null>(null);
  const servers = job?.servers ?? [];
  const failed = failedServers(job);
  const ok = servers.length - failed.length;
  const detail = servers.find(s => s.server === server) ?? null;
  return (
    <Stack gap={16}>
      {job?.status === 'queued' && <Text variant="body-sm" tone="mute">{t('settings.enrolment.job.queued')}</Text>}
      {job?.error && <Alert tone="neg">{job.error}</Alert>}
      {results && (
        <>
          {(job?.status === 'running' || job?.status === 'queued') && <Alert tone="info">{t('settings.enrolment.job.running')}</Alert>}
          {job?.status === 'succeeded' && <Alert tone="pos">{t('settings.enrolment.job.succeeded', { n: ok })}</Alert>}
          {job?.status === 'partial' && (
            <Alert tone="warn">{t('settings.enrolment.job.partial', { n: ok, total: servers.length, failed: failed.join(', ') })}</Alert>
          )}
          {job?.status === 'failed' && <Alert tone="neg">{t('settings.enrolment.job.failed')}</Alert>}
        </>
      )}
      <DataTable
        label={t('settings.enrolment.job.table')}
        rows={servers}
        rowKey="server"
        loading={loading}
        toolbar={false}
        density="compact"
        height={height}
        onRowClick={(s: Progress) => setServer(s.server)}
        activeRowKey={server ?? undefined}
        columns={[
          { key: 'server', label: t('settings.access.columns.server'), mono: true, width: 220 },
          // The server, its state, then how far it went.
          { key: 'status', label: t('settings.enrolment.columns.result'), width: 130, render: (s: Progress) => <Badge tone={RESULT_TONES[s.status]}>{t(`settings.enrolment.result.${s.status}`)}</Badge> },
          {
            key: 'done', label: t('settings.enrolment.columns.progress'), grow: true,
            render: (s: Progress) => (
              <ProgressBar value={(s.done / s.total) * 100} label={t('settings.enrolment.tasksDone', { n: s.done, total: s.total })} ariaLabel={t('settings.enrolment.columns.progress')} />
            ),
          },
        ]}
      />
      <Text variant="body-sm" tone="mute">{t('settings.enrolment.job.openServer')}</Text>

      <Drawer
        open={Boolean(detail)}
        onClose={() => setServer(null)}
        eyebrow={t('settings.enrolment.server.eyebrow')}
        title={detail?.server ?? ''}
        subtitle={detail ? t('settings.enrolment.tasksDone', { n: detail.done, total: detail.total }) : undefined}
        size="xl"
        footer={<Button variant="secondary" onClick={() => setServer(null)}>{t('settings.enrolment.back')}</Button>}
      >
        {detail && (
          <Timeline
            label={t('settings.enrolment.server.tasks')}
            items={detail.tasks.map(task => ({
              id: task.id,
              title: task.label,
              status: task.status,
              description: task.status === 'error' && detail.error ? `${detail.error.message.replace(/\.$/, '')}. ${detail.error.hint}` : task.status === 'current' ? t('settings.enrolment.server.current') : undefined,
            }))}
          />
        )}
      </Drawer>
    </Stack>
  );
}
